import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { DeliveryNote } from '../../types';
import Button from '../Form/Button';

interface DeliveryNoteLetterGeneratorProps {
  deliveryNote: DeliveryNote;
  /** e.g. "FIN-Req-01-2026 - Petro Provider", shown on the printed form */
  requisitionInfo?: string;
  onClose: () => void;
  onGenerate: (data: any) => void;
}

const DeliveryNoteLetterGenerator: React.FC<DeliveryNoteLetterGeneratorProps> = ({
  deliveryNote,
  requisitionInfo,
  onClose,
  onGenerate
}) => {
  const [generatedTemplate, setGeneratedTemplate] = useState<string>('');
  const [language, setLanguage] = useState<'en' | 'ar'>('en');

  const {
    formState: { isSubmitting }
  } = useForm();

  const generateTemplate = useCallback(() => {
    const template = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Delivery Note - ${deliveryNote.deliveryNoteNumber}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .company-logo { text-align: center; margin-bottom: 20px; }
        .title { font-size: 18px; font-weight: bold; text-align: center; margin: 20px 0; }
        .info-section { margin: 20px 0; }
        .info-row { display: flex; margin: 10px 0; }
        .info-label { font-weight: bold; min-width: 150px; }
        .technical-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        .technical-table th, .technical-table td { border: 1px solid #000; padding: 8px; text-align: left; }
        .technical-table th { background-color: #f0f0f0; font-weight: bold; }
        .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
        .signature-box { width: 45%; border: 1px solid #000; padding: 20px; }
        .signature-title { font-weight: bold; margin-bottom: 20px; }
        .signature-line { border-bottom: 1px solid #000; margin: 20px 0; height: 30px; }
        .page-footer { text-align: center; margin-top: 30px; border-top: 1px solid #000; padding-top: 10px; }
        @media print {
            body { margin: 0; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="company-logo">
        <h2>Petromarine</h2>
        <p>Offshore Projects Support Co.</p>
    </div>
    
    <div class="title">DELIVERY NOTE</div>
    
    <div class="info-section">
        <div class="info-row">
            <span class="info-label">Work Site / Vessel:</span>
            <span>${deliveryNote.workSite || ''}</span>
            <span style="margin-left: 100px; font-weight: bold;">Date:</span>
            <span>${deliveryNote.date ? new Date(deliveryNote.date).toLocaleDateString() : ''}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Project / Location:</span>
            <span>${deliveryNote.projectLocation || ''}</span>
            <span style="margin-left: 100px; font-weight: bold;">Note No.:</span>
            <span>${deliveryNote.noteNo || ''}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Requisition:</span>
            <span>${requisitionInfo || deliveryNote.requisitionId || ''}</span>
        </div>
    </div>
    
    <div style="margin: 20px 0; font-weight: bold;">Technical Part</div>
    
    <table class="technical-table">
        <thead>
            <tr>
                <th style="width: 40px;">No</th>
                <th>Type</th>
                <th style="width: 70px;">Qty</th>
                <th style="width: 70px;">ROB</th>
                <th style="width: 80px;">App Qty</th>
                <th style="width: 90px;">Unit Price</th>
                <th style="width: 90px;">Deliver Qty</th>
                <th style="width: 80px;">U. Size</th>
            </tr>
        </thead>
        <tbody>
            ${(deliveryNote.technicalPart || []).map((item: any) => `
                <tr>
                    <td>${item.no ?? ''}</td>
                    <td>${item.type ?? ''}</td>
                    <td>${item.qty ?? ''}</td>
                    <td>${item.rob ?? ''}</td>
                    <td>${item.appQty ?? ''}</td>
                    <td>${item.unitPrice != null ? Number(item.unitPrice).toLocaleString() : ''}</td>
                    <td>${item.quantity ?? ''}</td>
                    <td>${item.unitSize ?? ''}</td>
                </tr>
            `).join('')}
            ${Array.from({ length: Math.max(0, 15 - (deliveryNote.technicalPart?.length || 0)) }, (_, i) => `
                <tr>
                    <td>${(deliveryNote.technicalPart?.length || 0) + i + 1}</td>
                    <td></td><td></td><td></td><td></td><td></td><td></td><td></td>
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="signatures">
        <div class="signature-box">
            <div class="signature-title">Delivered BY:</div>
            <div style="margin: 20px 0;">
                <div style="font-weight: bold;">Signature</div>
                <div class="signature-line">${deliveryNote.deliveredBy?.signature || ''}</div>
            </div>
            <div style="margin: 20px 0;">
                <div style="font-weight: bold;">Name</div>
                <div class="signature-line">${deliveryNote.deliveredBy?.name || ''}</div>
            </div>
            <div style="margin: 20px 0;">
                <div style="font-weight: bold;">Stamp</div>
                <div class="signature-line">${deliveryNote.stampDeliveredBy || ''}</div>
            </div>
        </div>
        
        <div class="signature-box">
            <div class="signature-title">Delivered To: ${deliveryNote.deliveredTo?.zoom || ''}</div>
            <div style="margin: 20px 0;">
                <div style="font-weight: bold;">Signature</div>
                <div class="signature-line">${deliveryNote.deliveredTo?.signature || ''}</div>
            </div>
            <div style="margin: 20px 0;">
                <div style="font-weight: bold;">Name</div>
                <div class="signature-line">${deliveryNote.deliveredTo?.name || ''}</div>
            </div>
            <div style="margin: 20px 0;">
                <div style="font-weight: bold;">Stamp</div>
                <div class="signature-line">${deliveryNote.stampDeliveredTo || ''}</div>
            </div>
        </div>
    </div>
    
    <div class="page-footer">
        <div style="display: flex; justify-content: space-between;">
            <span>Petromarine Offshore Projects Support Co.</span>
            <span>${deliveryNote.pageNumber || ''}</span>
        </div>
    </div>
    
    <div class="no-print" style="margin-top: 30px; text-align: center;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Print Delivery Note
        </button>
    </div>
</body>
</html>
    `;
    
    setGeneratedTemplate(template);
  }, [deliveryNote, requisitionInfo]);

  useEffect(() => {
    generateTemplate();
  }, [generateTemplate]);

  const handleGenerate = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(generatedTemplate);
      printWindow.document.close();
      // Wait for the document to render before opening the print dialog
      setTimeout(() => { printWindow.focus(); printWindow.print(); }, 400);
    }
    onGenerate({ template: generatedTemplate, language });
  };

  const handleDownload = () => {
    const blob = new Blob([generatedTemplate], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `delivery-note-${deliveryNote.deliveryNoteNumber}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Generate Delivery Note</h1>
          <p className="text-gray-600">
            Delivery Note: {deliveryNote.deliveryNoteNumber} - {deliveryNote.vessel}
          </p>
        </div>
      </div>

      {/* Language Selection */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Template Options</h2>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="en"
                    checked={language === 'en'}
                    onChange={(e) => setLanguage(e.target.value as 'en' | 'ar')}
                    className="mr-2"
                  />
                  English
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="ar"
                    checked={language === 'ar'}
                    onChange={(e) => setLanguage(e.target.value as 'en' | 'ar')}
                    className="mr-2"
                  />
                  Arabic
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
        </div>
        <div className="card-body">
          {/* Sandboxed in an iframe so the document's styles and print button
              never touch (or print) the surrounding app layout */}
          <iframe
            title="Delivery note preview"
            srcDoc={generatedTemplate}
            className="w-full border rounded-lg bg-white"
            style={{ height: '800px' }}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <Button type="button" onClick={onClose} variant="secondary">
          Cancel
        </Button>
        <Button type="button" onClick={handleDownload} variant="secondary">
          Download HTML
        </Button>
        <Button 
          type="button" 
          onClick={handleGenerate} 
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Generating...' : 'Print Delivery Note'}
        </Button>
      </div>
    </div>
  );
};

export default DeliveryNoteLetterGenerator;
